<template>
  <div>
    <!-- Header -->
    <v-app-bar app color="white" elevation="0" height="80" class="dealer-header" flat>
      <v-app-bar-nav-icon @click="toggleDrawer" class="hamburger-icon ml-4">
        <v-icon class="material-icons-round">menu</v-icon>
      </v-app-bar-nav-icon>

      <v-container class="d-flex align-center px-6">
        <!-- Logo -->
        <div class="dealer-logo">
          <router-link to="/dealers" class="logo-link">
            <img
              src="@/assets/img/logo.png"
              alt="AutoSyncX™"
              height="50"
              class="logo-image"
            />
            <div class="logo-text">
              <span class="brand-name">AutoSyncX™</span>
              <span class="dealer-badge">Dealer Portal</span>
            </div>
          </router-link>
        </div>

        <v-spacer></v-spacer>

        <!-- User Menu -->
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="user-menu-btn"
              text
              v-bind="attrs"
              v-on="on"
            >
              <v-avatar size="32" class="mr-2">
                <v-icon class="material-icons-round">account_circle</v-icon>
              </v-avatar>
              <div class="user-info d-none d-sm-block">
                <div class="user-name">{{ userName }}</div>
                <div class="business-name">{{ businessName }}</div>
              </div>
              <v-icon class="ml-2 material-icons-round">expand_more</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>{{ userName }}</v-list-item-title>
                <v-list-item-subtitle>{{ businessName }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item @click="logout">
              <v-list-item-icon>
                <v-icon class="material-icons-round">logout</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Logout</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-container>
    </v-app-bar>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      app
      width="250"
      class="dealer-sidebar"
      color="white"
      fixed
    >
      <v-list nav class="sidebar-nav pt-4">
        <v-list-item
          v-for="item in navigationItems"
          :key="item.title"
          :class="{ 'v-list-item--active': activeNavItem === item.title }"
          @click="setActiveNavItem(item)"
          class="sidebar-nav-item mx-2 mb-1"
          link
          rounded
        >
          <v-list-item-icon class="mr-3">
            <v-icon class="material-icons-round sidebar-icon">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="sidebar-nav-title">{{ item.title }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action v-if="item.badge && navigationStats[item.badge] > 0">
            <v-chip
              small
              color="error"
              text-color="white"
              class="nav-badge"
            >
              {{ navigationStats[item.badge] }}
            </v-chip>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </div>
</template>

<script>

export default {
  name: 'HeaderDealerDashboard',
  props: {
    userName: {
      type: String,
      default: 'Dealer'
    },
    businessName: {
      type: String,
      default: 'Your Business'
    }
  },
  data() {
    return {
      drawer: true,
      activeNavItem: 'Dashboard',
      navigationStats: {
        pending_commissions: 0,
        new_reviews: 0,
        pending_payouts: 0
      },
      navigationItems: [
        { title: 'Dashboard', icon: 'dashboard', key: 'dashboard' },
        { title: 'Commission', icon: 'account_balance_wallet', key: 'commission', badge: 'pending_commissions' },
        { title: 'Inventory', icon: 'inventory', key: 'inventory' },
        { title: 'Distribution', icon: 'local_shipping', key: 'distribution' },
        { title: 'Referring Agents', icon: 'people', key: 'agents' },
        // { title: 'Reviews', icon: 'star', key: 'reviews', badge: 'new_reviews' },
        { title: 'Payouts', icon: 'payment', key: 'payouts', badge: 'pending_payouts' },
        { title: 'Profile', icon: 'person', key: 'profile' },
        { title: 'Settings', icon: 'settings', key: 'settings' }
      ]
    }
  },
  watch: {
    '$route'() {
      this.setActiveNavFromRoute()
    }
  },
  mounted() {
    this.loadNavigationStats()
    this.setActiveNavFromRoute()
    // Refresh stats every 5 minutes
    setInterval(this.loadNavigationStats, 300000)
  },
  methods: {
    async loadNavigationStats() {
      try {
        const token = localStorage.getItem('token')
        if (!token) return

        const response = await axios.get('/api/dealer/navigation-stats', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.flag === 1) {
          this.navigationStats = response.data.data
        }
      } catch (error) {
        console.error('Error loading navigation stats:', error)
      }
    },

    toggleDrawer() {
      this.drawer = !this.drawer
    },

    setActiveNavItem(item) {
      this.activeNavItem = item.title
      this.drawer = false // Close drawer after selection on mobile

      // Navigate to the appropriate route
      if (item.key === 'dashboard') {
        this.$router.push('/dealers/dashboard')
      } else if (item.key === 'commission') {
        this.$router.push('/dealers/commission')
      } else if (item.key === 'inventory') {
        this.$router.push('/dealers/inventory')
      } else if (item.key === 'distribution') {
        this.$router.push('/dealers/distribution')
      } else if (item.key === 'agents') {
        this.$router.push('/dealers/referring-agents')
      } else if (item.key === 'reviews') {
        // TODO: Add reviews route
        this.$toast.info('Reviews page coming soon!')
      } else if (item.key === 'payouts') {
        this.$router.push('/dealers/payouts')
      } else if (item.key === 'profile') {
        this.$router.push('/dealers/profile')
      } else if (item.key === 'settings') {
        this.$router.push('/dealers/settings')
      }

      // Emit navigation event to parent
      this.$emit('navigate', item)
    },

    setActiveNavFromRoute() {
      // Set active nav item based on current route
      const currentPath = this.$route.path
      if (currentPath === '/dealers/dashboard') {
        this.activeNavItem = 'Dashboard'
      } else if (currentPath === '/dealers/commission') {
        this.activeNavItem = 'Commission'
      } else if (currentPath.includes('/dealers/inventory')) {
        this.activeNavItem = 'Inventory'
      } else if (currentPath.includes('/dealers/distribution')) {
        this.activeNavItem = 'Distribution'
      } else if (currentPath.includes('/dealers/referring-agents')) {
        this.activeNavItem = 'Referring Agents'
      } else if (currentPath.includes('/dealers/reviews')) {
        this.activeNavItem = 'Reviews'
      } else if (currentPath.includes('/dealers/payouts')) {
        this.activeNavItem = 'Payouts'
      } else if (currentPath.includes('/dealers/profile')) {
        this.activeNavItem = 'Profile'
      } else if (currentPath.includes('/dealers/settings')) {
        this.activeNavItem = 'Settings'
      }
    },

    logout() {
      // Clear all localStorage data
      localStorage.removeItem('token')
      localStorage.removeItem('user_id')
      localStorage.removeItem('user_flag')
      localStorage.removeItem('user_name')
      localStorage.removeItem('user_email')
      localStorage.removeItem('business_name')
      localStorage.removeItem('dealer_id')
      localStorage.removeItem('dealer_status')

      this.$toast.success('Logged out successfully', {
        position: 'top-right'
      })

      this.$router.push('/dealers/login')
    }
  }
}
</script>

<style scoped>
/* Header Styles */
.dealer-header {
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.dealer-logo {
  display: flex;
  align-items: center;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 12px;
}

.logo-image {
  transition: transform 0.3s ease;
}

.logo-image:hover {
  transform: scale(1.05);
}

.logo-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.brand-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1;
  margin-bottom: 2px;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.02em;
}

.dealer-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
}

.user-menu-btn {
  color: #4a5568 !important;
  font-weight: 500 !important;
  text-transform: none !important;
  border-radius: 12px !important;
  padding: 8px 16px !important;
  transition: all 0.3s ease !important;
}

.user-menu-btn:hover {
  background-color: rgba(59, 130, 246, 0.08) !important;
  color: #3b82f6 !important;
}

.user-info {
  text-align: left;
  margin-left: 8px;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a202c;
  line-height: 1.2;
}

.business-name {
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.2;
}

/* Hamburger Menu */
.hamburger-icon {
  color: #4a5568 !important;
}

/* Sidebar Styles */
.dealer-sidebar {
  border-right: 1px solid #e0e0e0;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
}

.sidebar-nav {
  padding-top: 8px;
}

.sidebar-nav-item {
  margin-bottom: 4px;
  border-radius: 8px !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.sidebar-nav-item:hover {
  background-color: #f3f4f6 !important;
  transform: translateX(4px);
}

.v-list-item--active {
  background-color: #3b82f6 !important;
  color: white !important;
}

.v-list-item--active .sidebar-nav-title,
.v-list-item--active .sidebar-icon {
  color: white !important;
}

.sidebar-nav-title {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #374151 !important;
}

.sidebar-icon {
  color: #6b7280 !important;
  font-size: 20px !important;
}

.nav-badge {
  font-size: 10px !important;
  height: 18px !important;
  min-width: 18px !important;
}

/* Material Icons styling */
.material-icons-round {
  font-family: 'Material Icons Round';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

/* Responsive Design */
@media (max-width: 960px) {
  .logo-text {
    display: none;
  }

  .user-info {
    display: none !important;
  }
}

@media (max-width: 600px) {
  .dealer-header .v-container {
    padding: 0 16px;
  }
}
</style>
